import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { t } from 'ttag';
import debounce from 'lodash/debounce';
import difference from 'lodash/difference';
import { useHistory, useLocation } from 'react-router-dom';

import CloseIcon from '@svg/close.svg';
import { FILTER_QUERIES } from '@constants';
import { useDesktopOrWider } from '@utils/mediaQuery';
import { Button } from '@ui/Button';
import {
  enumerate,
  prepareAppliedFiltersForAnalytics,
  setAnaliticAppliedFiltersToStorage,
  useCatalogRouteMatch,
  useCollectionRouteMatch,
  useIsClient,
} from '@utils/common';
import { toggleArrayItem } from '@utils/arrays';
import {
  sendFeedFilterApplyEvent,
  sendFilterApplyEvent,
  sendFilterBrandChoiceEvent,
} from '@utils/analytics';
import {
  ActiveFilters,
  MAX_FETCHED_BRANDS,
  useIndexedFilters,
  useProductFilters,
  useWithIndexedFilters,
} from '@hook/productFilters';
import { FeedSizeFilter } from '@components/FeedSizeFilter';
import { FeedPriceFilter, FeedPriceFilterProps } from '@components/FeedPriceFilter';
import { FeedColorFilter, ColorOptions } from '@components/FeedColorFilter';
import { FeedConditionFilter } from '@components/FeedConditionFilter';
import { FeedCatalogFilter, FeedCatalogFilterItems } from '@components/FeedCatalogFilter';
import { FeedTagFilter, TagFilter } from '@components/FeedTagFilter';
import { FeedBrandFilterContainer, Option as BrandOption } from '@containers/catalog/FeedBrandFilter';
import { FeedAsideSkeleton } from '@components/FeedAside/FeedAsideSkeleton';
import { FilterType } from '@v5Models';
import { useFiltersOrderQuery } from '@hook/productFilters/graphql.v5.generated';
import {
  useFilterTopBrandsQuery,
} from '@containers/catalog/FeedBrandFilter/gql/graphql.v5.generated';
import { ProductCharacteristicsFilter, Option as CharacteristicOption } from '@components/ProductCharacteristicsFilter';
import { FeedCityFilterContainer } from '@containers/catalog/FeedCityFilter';
import { useIsAuth } from '@hook/useIsAuth';
import { notifyError } from '@utils/notifications';
import { useAppContext } from '@contexts/AppContext';

import { FeedAsidePlacement } from './utils';

import s from './FeedAside.sss';

type FeedAsideProps = {
  isMobile: boolean;
  total: number;
  catalogs?: FeedCatalogFilterItems;
  isSizeFilterEnable: boolean;
  isBrandFilterEnable: boolean;
  isColorFilterEnable: boolean;
  isPriceFilterEnable: boolean;
  isConditionFilterEnable: boolean;
  isCharacteristicFilterEnable?: boolean;
  isOnSaleFilterEnable?: boolean;
  isFreeShippingFilterEnable?: boolean;
  isCityFilterEnable?: boolean;
  sizeCatalogSlug?: string | null;
  dynamicCollectionSlug?: string | null;
  placement?: FeedAsidePlacement;
  skip?: boolean | null
  onClose(): void;
};

export const FeedAside: React.FC<FeedAsideProps> = ({
  isMobile,
  onClose,
  total,
  catalogs,
  isSizeFilterEnable,
  isBrandFilterEnable,
  isColorFilterEnable,
  isPriceFilterEnable,
  isConditionFilterEnable,
  isCharacteristicFilterEnable = false,
  isCityFilterEnable = false,
  isFreeShippingFilterEnable = true,
  isOnSaleFilterEnable = true,
  sizeCatalogSlug,
  dynamicCollectionSlug,
  placement,
  skip,
}) => {
  const history = useHistory();
  const location = useLocation();
  const isClient = useIsClient();
  const isDesktopOrWider = useDesktopOrWider();
  const { isBot, isOutlet } = useAppContext();

  const {
    data: filtersOrderData,
    loading: filtersOrderLoading,
  } = useFiltersOrderQuery({
    variables: {
      catalogSlug: sizeCatalogSlug,
    },
    context: {
      v5: true,
      batch: true,
    },
    skip: !sizeCatalogSlug,
  });
  const filtersOrderRef = useRef(filtersOrderData);
  filtersOrderRef.current = filtersOrderLoading ? filtersOrderRef.current : filtersOrderData;

  const { data: topBrandsData } = useFilterTopBrandsQuery({
    context: {
      v5: true,
      batch: true,
    },
    variables: {
      catalogSlug: sizeCatalogSlug,
      dynamicCollectionSlug,
      isBot,
    },
    skip: !isBrandFilterEnable,
  });

  const withIndexedFilters = useWithIndexedFilters();
  const { generateIndexedFilterPath } = useIndexedFilters();

  const {
    filters,
    activeFilters,
    rawActiveFilters,
    loading,
    setFilter,
    clearAllFilters,
  } = useProductFilters({
    catalogSlug: sizeCatalogSlug,
    dynamicCollectionSlug,
    skip,
  });
  const catalogMatch = useCatalogRouteMatch();
  const collectionMatch = useCollectionRouteMatch();
  const isAuth = useIsAuth();

  const [priceFrom, setPriceFrom] = useState(activeFilters.priceFrom?.toString() || '');
  const [priceTo, setPriceTo] = useState(activeFilters.priceTo?.toString() || '');

  const {
    filterSizes,
    filterConditions,
    filterPrices,
    filterColors,
  } = filters || {};
  const {
    sizes,
    conditions,
    colors,
    brands,
    prices,
  } = rawActiveFilters;

  // if prices will change from another place(clear filter) we need update state
  useEffect(() => setPriceFrom(activeFilters.priceFrom?.toString() || ''), [activeFilters.priceFrom]);
  useEffect(() => setPriceTo(activeFilters.priceTo?.toString() || ''), [activeFilters.priceTo]);

  // handlers
  const changePath = (
    newFilters: Partial<ActiveFilters>,
  ): void => {
    const newCharacteristicsId = difference(newFilters.characteristics || [], rawActiveFilters.characteristics)[0];
    const filtersValueForAnalytics = prepareAppliedFiltersForAnalytics({
      activeFilters: {
        ...rawActiveFilters,
        ...newFilters,
      },
      characteristics: filters?.filterCharacteristic,
    });

    setAnaliticAppliedFiltersToStorage({
      pathname: location.pathname,
      filters: filtersValueForAnalytics,
    });

    if (catalogMatch || isOutlet) {
      sendFilterApplyEvent({
        namespace: catalogMatch?.params?.namespace || 'clothes',
        categorySlug: catalogMatch && 'slug' in catalogMatch.params ? catalogMatch?.params.slug : undefined,
        ...(!!newCharacteristicsId && {
          item_characteristics: String(newCharacteristicsId),
        }),
        ...(!!filtersValueForAnalytics && {
          filters: filtersValueForAnalytics,
        }),
      });
    } else if (collectionMatch) {
      sendFilterApplyEvent({
        collection: collectionMatch.params.slug,
        ...(!!newCharacteristicsId && {
          item_characteristics: String(newCharacteristicsId),
        }),
        ...(!!filtersValueForAnalytics && {
          filters: filtersValueForAnalytics,
        }),
      });
    }

    setFilter(newFilters);
  };
  const changePathRef = useRef(changePath);
  changePathRef.current = changePath;

  const changePriceFilter = useMemo(
    () => (
      // Such dances with ref and memo required to call actual version of `changePath` func
      // and don't cancel debounce if `changePath` changes
      (debounce(
        (...args: Parameters<typeof changePath>) => changePathRef.current(...args),
        2000,
      ))
    ),
    [],
  );

  // Debounce cleanup
  useEffect(
    () => () => changePriceFilter.cancel(),
    [changePriceFilter],
  );

  const selectSize = (id: number) => {
    changePath({ sizes: toggleArrayItem(id, sizes) });
  };

  const handleBrandsChange = (brand: BrandOption) => {
    const newBrands = toggleArrayItem(brand.id, brands);

    // If you add more than allowed brands, show error
    if (newBrands.length > MAX_FETCHED_BRANDS) {
      notifyError(t`Можна обрати до ${MAX_FETCHED_BRANDS} брендів`);
      return;
    }

    sendFilterBrandChoiceEvent({
      brandName: brand.name,
      referrer: 'filter_feed',
    });

    if (brand.isIndexed && withIndexedFilters) {
      history.push(
        generateIndexedFilterPath({ brands: [brand.id] }),
      );
    } else {
      changePath({ brands: newBrands });
    }
  };

  const selectCondition = (id: number) => {
    changePath({ conditions: toggleArrayItem(id, conditions) });
  };

  const handleCharacteristicsChange = (characteristic: CharacteristicOption) => {
    if (characteristic.isIndexed && withIndexedFilters) {
      history.push(
        generateIndexedFilterPath({ characteristics: [characteristic.id] }),
      );
    } else {
      changePath({
        characteristics: toggleArrayItem(characteristic.id, rawActiveFilters.characteristics),
      });
    }
  };

  const handleColorsChange = (color: ColorOptions) => {
    if (color.isIndexed && withIndexedFilters) {
      history.push(
        generateIndexedFilterPath({ colors: [Number(color.id)] }),
      );
    } else {
      changePath({ colors: toggleArrayItem(Number(color.id), colors) });
    }
  };

  const selectPrice = (id: number) => {
    changePath({
      prices: toggleArrayItem(id, prices),
      priceFrom: undefined,
      priceTo: undefined,
    });
  };

  const onChangePriceInput: FeedPriceFilterProps['onChange'] = (name, e) => {
    if (name === 'from') {
      setPriceFrom(e.target.value);
      changePriceFilter({ priceFrom: +e.target.value || undefined });
    } else if (name === 'to') {
      setPriceTo(e.target.value);
      changePriceFilter({ priceTo: +e.target.value || undefined });
    }
  };

  const handleTagChange = (value: TagFilter['value']) => {
    if (value === 'freeShipping') {
      sendFeedFilterApplyEvent('filter_free_shipping');
    } else if (value === 'isOnSale') {
      sendFeedFilterApplyEvent('filter_sale');
    } else if (value === 'ukrainian') {
      sendFeedFilterApplyEvent('filter_made_in_ua', 'filter_tag');
    }

    changePath({ [value]: !rawActiveFilters[value] ? true : undefined });
  };

  const handleCategorySelect = () => {
    if (catalogMatch) {
      sendFilterApplyEvent({
        namespace: catalogMatch.params.namespace,
        categorySlug: 'slug' in catalogMatch.params ? catalogMatch.params.slug : undefined,
      });
    } else if (collectionMatch) {
      sendFilterApplyEvent({
        collection: collectionMatch.params.slug,
      });
    }
  };

  if (loading || !filters || (filtersOrderLoading && !filtersOrderRef.current)) {
    return <FeedAsideSkeleton />;
  }

  const simpleFiltersMap = new Map([
    [
      FilterType.Size,
      filterSizes && isSizeFilterEnable && (
        <FeedSizeFilter
          key="size"
          options={filterSizes}
          sizeGroupTitle={filtersOrderRef.current?.catalog.sizeGroup?.name}
          actives={sizes}
          select={selectSize}
        />
      ),
    ],
    [
      FilterType.Condition,
      filterConditions && isConditionFilterEnable && (
        <FeedConditionFilter
          key="condition"
          options={filterConditions}
          actives={conditions}
          select={selectCondition}
          isMobile={isMobile}
        />
      ),
    ],
    [
      FilterType.Price,
      filterPrices && isPriceFilterEnable && (
        <FeedPriceFilter
          key="price"
          options={filterPrices}
          selectedPrices={rawActiveFilters.prices}
          select={selectPrice}
          onChange={onChangePriceInput}
          valueFrom={priceFrom}
          valueTo={priceTo}
          isMobile={isMobile}
        />
      ),
    ],
    [
      FilterType.Color,
      filterColors && isColorFilterEnable && (
        <FeedColorFilter
          key="color"
          options={filterColors}
          actives={colors}
          onChange={handleColorsChange}
          isMobile={isMobile}
        />
      ),
    ],
    [
      FilterType.Brand,
      isBrandFilterEnable && (
        <FeedBrandFilterContainer
          key="brand"
          value={activeFilters.brands}
          onChange={handleBrandsChange}
          defaultOptions={{
            topBrands: topBrandsData?.filterTopBrands?.topBrands || [],
            brands: topBrandsData?.filterTopBrands?.brands || [],
          }}
        />
      ),
    ],
    [
      FilterType.City,
      isClient && isCityFilterEnable && isAuth && (
        <FeedCityFilterContainer
          key="city"
          value={activeFilters.cities.length ? activeFilters.cities[0].id : undefined}
          onChange={(cityId) => changePath({ cities: cityId ? [cityId] : [] })}
        />
      ),
    ],
  ]);
  const characteristicFiltersMap = new Map(
    isCharacteristicFilterEnable && sizeCatalogSlug && filters.filterCharacteristic
      ? filters.filterCharacteristic?.map(
        (characteristic) => ([
          characteristic.id,
          (
            <ProductCharacteristicsFilter
              onChange={handleCharacteristicsChange}
              value={rawActiveFilters.characteristics}
              key={`characteristic-${characteristic.id}`}
              title={characteristic.title}
              items={characteristic.choices}
              className={s.filter}
            />
          ),
        ]),
      )
      : [],
  );

  return (
    (
      <aside className={classNames([s.root, { [s.activeFilter]: isMobile }])}>
        <p className={s.title}>
          <CloseIcon
            className={s.cross}
            width='24'
            height='24'
            onClick={onClose}
          />
          {t`Фільтри`}
        </p>
        {isClient && !isDesktopOrWider && location.search && (
        <div className={s.clearAllBlock}>
          <div
            className={classNames(s.clearAll, 'js-catalog-filter-remove-all')}
            onClick={clearAllFilters}
          >
            {t`Скинути фільтри`}
          </div>
        </div>
        )}
        {!!catalogs?.length && (
        <FeedCatalogFilter
          className={s.catalogs}
          items={catalogs}
          filterParam={FILTER_QUERIES.CATALOG}
          filterValueKey="slug"
          initialTreeLevel={placement && ['shop', 'favourite'].includes(placement) ? 2 : 1}
          onClick={handleCategorySelect}
        />
        )}
        <section className={s.section}>
          <FeedTagFilter
            value={{
              freeShipping: activeFilters.freeShipping,
              isOnSale: activeFilters.isOnSale,
              ukrainian: Boolean(activeFilters.ukrainian),
            }}
            isFreeShippingEnabled={isFreeShippingFilterEnable}
            isOnSaleEnabled={isOnSaleFilterEnable}
            onClick={handleTagChange}
          />
        </section>

        {
          filtersOrderRef.current?.catalog?.filterTypes?.map(
            ({ type, filterEntityId }) => {
              if (type === FilterType.Characteristic && filterEntityId) {
                const filter = characteristicFiltersMap.get(+filterEntityId);
                if (filter) {
                  characteristicFiltersMap.delete(+filterEntityId);
                  return filter;
                }
              }
              const filter = simpleFiltersMap.get(type);
              if (filter) {
                simpleFiltersMap.delete(type);
                return filter;
              }

              return undefined;
            },
          )
        }

        {
          Array.from(simpleFiltersMap.values()).map((filter) => filter)
        }

        {
          Array.from(characteristicFiltersMap?.values() || []).map((filter) => filter)
        }

        {isClient && !isDesktopOrWider && (
        <Button
          className={s.buttonSeeFilters}
          theme="primary"
          onClick={onClose}
        >
          {t`Показати`}
          {' '}
          {total}
          {' '}
          {enumerate(Number(total), [t`річ`, t`речі`, t`речей`])}
        </Button>
        )}
      </aside>
    )
  );
};
